import axios from 'axios'
import toast from 'react-hot-toast'
import { getFilialIdHeader } from './filial'
import { getCanalHeader } from './canal'
import { isReadonlyActive } from './readonly'

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
})

// Escritas que na verdade são LEITURAS (geram PDF/relatório, buscam, simulam) —
// liberadas mesmo no Modo Visualização.
const ESCRITA_LIBERADA = /(\/auth\/(login|me)|\/pdf|relatorio|export|exportar|buscar|consultar|preview|calcul|simul|\/ia-)/i

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('pain_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  const fid = getFilialIdHeader()
  if (fid) config.headers['X-Filial-Id'] = fid
  // Canal de PDV (balcao×online) — isolamento por canal (09/07). Só envia quando a
  // visão é de UM canal; em 'ambos' omite (backend decide: lockdown se o usuário só
  // pode 1 canal, senão os dois). O servidor sempre impõe os canais permitidos.
  const canal = getCanalHeader()
  if (canal) config.headers['X-Canal'] = canal
  // Modo Visualização (admin): bloqueia toda escrita no sistema inteiro.
  const metodo = (config.method || 'get').toLowerCase()
  if (['post', 'put', 'patch', 'delete'].includes(metodo)
      && isReadonlyActive() && !ESCRITA_LIBERADA.test(config.url || '')) {
    // Erro "amigável" 403: o onError das telas mostra o detail (toast único).
    // O response interceptor ignora via flag __readonly pra não duplicar. O banner
    // fixo no topo cobre os casos sem tratamento de erro.
    return Promise.reject({
      __readonly: true,
      response: { status: 403, data: { detail: 'Modo Visualização — edição bloqueada. Saia do modo para editar.' } },
      config,
    })
  }
  return config
})

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (axios.isCancel(err) || err?.__readonly) return Promise.reject(err)  // Modo Visualização: toast já mostrado
    const status = err.response?.status
    const detail = err.response?.data?.detail || err.message
    if (status === 401) {
      const url = err.config?.url || ''
      const token = localStorage.getItem('pain_token')
      // Se nao tem token OU eh endpoint de auth, de fato desloga
      if (!token || url.includes('/auth/me') || url.includes('/auth/login')) {
        localStorage.removeItem('pain_token')
        localStorage.removeItem('pain_user')
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
      } else {
        // 401 pontual — so avisa, nao desloga
        toast.error('Sessão: acesso negado a este recurso')
      }
    } else if (status >= 400) {
      const url = err.config?.url || ''
      const detailStr = typeof detail === 'string' ? detail : ''
      const isMarketplaceEndpoint = url.startsWith('/ml/') || url.includes('/marketplace/ml/')
        || url.startsWith('/tray/') || url.includes('/marketplace/tray/')
      // Silencia toasts de "Sem conexão ML/Tray ativa" — esses endpoints retornam 400
      // quando empresa não tem conexão configurada, e não há motivo de poluir a UI.
      const isSemConexaoMsg = /sem conex[aã]o.*ativ[ao]/i.test(detailStr)
      if (isMarketplaceEndpoint && (status === 400 || isSemConexaoMsg)) {
        return Promise.reject(err)
      }
      toast.error(detailStr || 'Erro na requisição')
    }
    return Promise.reject(err)
  }
)

export function formatMoney(v: number | string | undefined | null): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

// Timezone do sistema: o backend grava datetimes em UTC "naïve" (sem marca de
// fuso). Pra exibir, marcamos como UTC e convertemos pra horário de Brasília.
const TZ_BR = 'America/Sao_Paulo'

// Garante que a string ISO seja interpretada como UTC: se não tiver marca de
// fuso (Z ou ±HH:MM), acrescenta 'Z'. (Datetime com hora; datas puras não usam.)
function _comoUtc(s: string): string {
  return /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : s.replace(' ', 'T') + 'Z'
}

export function formatDate(s: string | null | undefined): string {
  if (!s) return '-'
  try {
    // Strings YYYY-MM-DD (date puro) precisam ser parseadas como local, senão
    // new Date() interpreta como UTC meia-noite e volta 1 dia em fusos negativos.
    // Vale TAMBÉM pra datetime ISO sem timezone (ex: "2026-05-28T00:00:00") —
    // extraímos só o trecho YYYY-MM-DD pra não cair no parser do browser.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
    if (m) {
      const [, y, mo, d] = m
      return `${d}/${mo}/${y}`
    }
    return new Date(s).toLocaleDateString('pt-BR', { timeZone: TZ_BR })
  } catch { return s }
}

export function formatDateTime(s: string | null | undefined): string {
  if (!s) return '-'
  try { return new Date(_comoUtc(s)).toLocaleString('pt-BR', { timeZone: TZ_BR }) } catch { return s }
}

// Só a hora (HH:MM:SS) em horário de Brasília — pra "atualizado às ...".
export function formatTime(s: string | null | undefined): string {
  if (!s) return '-'
  try { return new Date(_comoUtc(s)).toLocaleTimeString('pt-BR', { timeZone: TZ_BR }) } catch { return s }
}
